import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import imageGenerationService, { GeneratedImage } from '../../services/api/imageGenerationService';

interface ImageVersionNavigatorProps {
  image: GeneratedImage;
  onSelect: (image: GeneratedImage) => void;
}

const ImageVersionNavigator: React.FC<ImageVersionNavigatorProps> = ({ image, onSelect }) => {
  const [versions, setVersions] = useState<GeneratedImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // 矢印での移動先は同じ版の系列にいるので取り直さない。取り直すと読み込み中（最大十数秒）は
  // 矢印が消え、押せたか分からず連打される原因になっていた。
  const versionsRef = useRef<GeneratedImage[]>([]);
  versionsRef.current = versions;

  useEffect(() => {
    let cancelled = false;
    if (versionsRef.current.some((version) => version.id === image.id)) return;

    const fetchVersions = async () => {
      if (!image.id || image.id <= 0) {
        setVersions([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await imageGenerationService.getImageVersions(image.id);
        if (!cancelled && response.success) {
          setVersions(response.data.versions);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch image versions:', err);
          setVersions([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchVersions();
    return () => {
      cancelled = true;
    };
  }, [image.id]);

  const currentIndex = useMemo(
    () => versions.findIndex((version) => version.id === image.id),
    [versions, image.id]
  );

  if (isLoading || versions.length <= 1 || currentIndex < 0) return null;

  const goTo = (nextIndex: number) => {
    const next = versions[nextIndex];
    if (next) onSelect(next);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => goTo(currentIndex - 1)}
        disabled={currentIndex === 0}
        aria-label="前のバージョン"
        className={`absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md shadow-lg transition-colors ${
          currentIndex === 0
            ? 'cursor-not-allowed bg-white/50 text-gray-400'
            : 'bg-white/90 text-gray-900 hover:bg-white'
        }`}
        title="前のバージョン"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 rounded-md bg-black bg-opacity-70 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
        v{currentIndex + 1} / {versions.length}
      </div>

      <button
        type="button"
        onClick={() => goTo(currentIndex + 1)}
        disabled={currentIndex === versions.length - 1}
        aria-label="次のバージョン"
        className={`absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md shadow-lg transition-colors ${
          currentIndex === versions.length - 1
            ? 'cursor-not-allowed bg-white/50 text-gray-400'
            : 'bg-white/90 text-gray-900 hover:bg-white'
        }`}
        title="次のバージョン"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
    </>
  );
};

export default ImageVersionNavigator;
